/**
 * The sign-in page's brand panel: CLIRNET navy with soft geometric shapes and
 * a small composition of white dashboard cards (a trend chart, a stat card, a
 * channel donut) with round content-type badges, then the product line.
 *
 * The page is generic (no tenant chosen yet), so the cards carry shapes, not
 * figures or labels: skeleton bars stand in for text and the series are made
 * up. Pure SVG + CSS; it plays once on load (keyframes in index.css) and
 * "reduce motion" shows it finished.
 */

import { Icon } from "../../components/ui/Icon.jsx";

const NAVY = "#0f2c60";
const BLUE = "#3c7abe";
const TEAL = "#2bb5a3";

// The trend card's two series on a 0–100 scale: "this period" and "previous".
const CURRENT = [22, 30, 26, 44, 38, 58, 50, 66, 61, 78];
const PREVIOUS = [18, 22, 28, 25, 34, 31, 40, 37, 46, 44];
const CW = 280;
const CH = 120;
const cx = (i, n) => 6 + (i / (n - 1)) * (CW - 12);
const cy = (v) => CH - 8 - (v / 100) * (CH - 20);
const path = (vals) => vals.map((v, i) => `${i ? "L" : "M"}${cx(i, vals.length).toFixed(1)},${cy(v).toFixed(1)}`).join(" ");
const LINE = path(CURRENT);
const AREA = `${LINE} L${cx(CURRENT.length - 1, CURRENT.length)},${CH} L${cx(0, CURRENT.length)},${CH} Z`;
const PEAK = CURRENT.length - 1;

const MINI_BARS = [40, 62, 48, 80, 56];
const MIX = [
  [NAVY, 0.46],
  [BLUE, 0.32],
  [TEAL, 0.22],
];
const R = 15;
const C = 2 * Math.PI * R;
// Each slice starts where the previous ones end.
const SLICES = MIX.map(([color, share], i) => ({
  color,
  len: share * C - 2,
  start: MIX.slice(0, i).reduce((s, [, f]) => s + f * C, 0),
}));

/** A rounded grey bar standing in for a line of text. */
function Bar({ w, className = "bg-[#e6e9f0]", h = "h-2" }) {
  return <span className={`block rounded-full ${h} ${className}`} style={{ width: w }} />;
}

function Badge({ icon, className, delay }) {
  return (
    <span className={`absolute grid size-12 animate-rise place-items-center rounded-full bg-white text-[#0f2c60] shadow-[0_10px_30px_-8px_rgb(8_20_50/0.45)] ${className}`} style={{ animationDelay: delay }}>
      <Icon name={icon} className="size-5" />
    </span>
  );
}

const card = "absolute animate-rise rounded-[18px] bg-white shadow-[0_24px_60px_-20px_rgb(8_20_50/0.55)]";

export default function LoginShowcase() {
  return (
    <aside
      className="relative hidden overflow-hidden rounded-[22px] text-white lg:flex lg:flex-col"
      style={{
        background: `linear-gradient(150deg, ${NAVY} 0%, #1b4a8f 58%, ${BLUE} 100%)`,
      }}
    >
      {/* Soft geometric planes, as in a printed brand sheet. */}
      <span aria-hidden className="absolute -top-24 -right-16 h-80 w-96 rotate-[-18deg] rounded-[48px] bg-white/[0.07]" />
      <span aria-hidden className="absolute -bottom-32 -left-20 h-96 w-[28rem] rotate-[24deg] rounded-[56px] bg-white/[0.06]" />
      <span aria-hidden className="absolute top-1/3 -left-24 h-56 w-56 rotate-12 rounded-[40px] bg-[#0b2350]/40" />

      <div aria-hidden className="relative flex flex-1 items-center justify-center px-10 pt-10">
        <div className="relative aspect-[44/36] w-full max-w-[440px]">
          {/* Trend card */}
          <div className={`${card} top-[12%] left-0 w-[70%] p-5`} style={{ animationDelay: "80ms" }}>
            <div className="flex items-center gap-3">
              <span className="grid size-9 place-items-center rounded-[11px] bg-[#e9eff9] text-[#0f2c60]">
                <Icon name="chart" className="size-[18px]" />
              </span>
              <span className="flex-1 space-y-1.5">
                <Bar w="58%" h="h-2.5" className="bg-[#0f2c60]/85" />
                <Bar w="36%" />
              </span>
            </div>
            <svg viewBox={`0 0 ${CW} ${CH}`} className="mt-4 block h-auto w-full overflow-visible">
              <defs>
                <linearGradient id="showcase-area" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BLUE} stopOpacity="0.28" />
                  <stop offset="100%" stopColor={BLUE} stopOpacity="0" />
                </linearGradient>
              </defs>
              {[0.25, 0.5, 0.75].map((f) => (
                <line key={f} x1="0" x2={CW} y1={CH * f} y2={CH * f} stroke="#eef0f5" strokeDasharray="3 5" />
              ))}
              <path d={AREA} fill="url(#showcase-area)" className="animate-[login-fade_700ms_ease-out_both]" style={{ animationDelay: "1000ms" }} />
              <path
                d={path(PREVIOUS)}
                fill="none"
                stroke={TEAL}
                strokeWidth="2"
                strokeDasharray="1"
                pathLength="1"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="animate-[login-draw_1300ms_cubic-bezier(0.4,0,0.2,1)_both]"
                style={{ animationDelay: "450ms" }}
              />
              <path
                d={LINE}
                fill="none"
                stroke={BLUE}
                strokeWidth="2.5"
                strokeDasharray="1"
                pathLength="1"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="animate-[login-draw_1300ms_cubic-bezier(0.4,0,0.2,1)_both]"
                style={{ animationDelay: "300ms" }}
              />
              <g className="animate-[login-fade_300ms_ease-out_both]" style={{ animationDelay: "1500ms" }}>
                <line x1={cx(PEAK, CURRENT.length)} x2={cx(PEAK, CURRENT.length)} y1={cy(CURRENT[PEAK])} y2={CH} stroke={BLUE} strokeOpacity="0.35" strokeDasharray="2 3" />
                <circle cx={cx(PEAK, CURRENT.length)} cy={cy(CURRENT[PEAK])} r="5" fill="#fff" stroke={BLUE} strokeWidth="2.5" />
              </g>
            </svg>
            <div className="mt-3 flex justify-between px-1">
              {CURRENT.map((_, i) => (
                <span key={i} className="size-1 rounded-full bg-[#d5dae4]" />
              ))}
            </div>
          </div>

          {/* Stat card */}
          <div className={`${card} top-0 right-0 w-[38%] p-4`} style={{ animationDelay: "220ms" }}>
            <Bar w="48%" className="bg-[#f2a541]" />
            <Bar w="72%" h="h-3.5" className="mt-2.5 bg-[#0f2c60]/85" />
            <div className="mt-4 flex h-12 items-end gap-1.5">
              {MINI_BARS.map((v, i) => (
                <span
                  key={i}
                  className="flex-1 origin-bottom animate-[login-bar_600ms_cubic-bezier(0.2,0.8,0.2,1)_both] rounded-[4px]"
                  style={{
                    height: `${v}%`,
                    background: i === 3 ? BLUE : "#e3e9f4",
                    animationDelay: `${500 + i * 70}ms`,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Channel mix card */}
          <div className={`${card} right-[4%] bottom-0 flex w-[50%] items-center gap-4 p-4`} style={{ animationDelay: "360ms" }}>
            <svg viewBox="0 0 40 40" className="size-16 shrink-0 -rotate-90">
              <circle cx="20" cy="20" r={R} fill="none" stroke="#eef0f5" strokeWidth="6" />
              {SLICES.map(({ color, len, start }, i) => (
                <circle
                  key={color}
                  cx="20"
                  cy="20"
                  r={R}
                  fill="none"
                  stroke={color}
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray={`${len} ${C}`}
                  strokeDashoffset={-start}
                  className="animate-[login-fade_500ms_ease-out_both]"
                  style={{ animationDelay: `${800 + i * 150}ms` }}
                />
              ))}
            </svg>
            <span className="flex-1 space-y-2.5">
              {MIX.map(([color], i) => (
                <span key={color} className="flex items-center gap-2">
                  <span className="size-2 shrink-0 rounded-full" style={{ background: color }} />
                  <Bar w={["80%", "62%", "46%"][i]} />
                </span>
              ))}
            </span>
          </div>

          <Badge icon="play" className="top-[4%] left-[52%]" delay="520ms" />
          <Badge icon="book" className="bottom-[22%] -left-3" delay="640ms" />
          <Badge icon="users" className="top-[50%] -right-4" delay="760ms" />
        </div>
      </div>

      <div className="relative px-10 pt-6 pb-10 xl:px-12">
        <h2 className="max-w-md animate-rise text-[28px] leading-[1.15] font-semibold tracking-[-0.02em] xl:text-[32px]" style={{ animationDelay: "200ms" }}>
          Every read, view and click, in one place.
        </h2>
        <p className="mt-3 max-w-md animate-rise text-[14px] leading-relaxed text-white/70 [@media(max-height:760px)]:hidden" style={{ animationDelay: "280ms" }}>
          Audience, content and campaign analytics for every CLIRNET tenant, scoped to what your role allows.
        </p>
        <ul className="mt-5 flex flex-wrap gap-2 [@media(max-height:700px)]:hidden">
          {["Acquisition", "Engagement", "Monitoring"].map((t, i) => (
            <li key={t} className="animate-rise rounded-full bg-white/12 px-3 py-1 text-[12px] font-medium text-white/90 ring-1 ring-white/15" style={{ animationDelay: `${340 + i * 60}ms` }}>
              {t}
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}
